/**
 * Instagram caption library, 5 ready-to-publish captions for Keystone.
 * Each maps to one of the 5 Instagram visual pillars defined in the
 * strategic playbook (Floor First · Tools Not Theatre · Cheese is Craft
 * · Excel vs Reality · The Team).
 *
 * Voice: visual, story-driven, slower than LinkedIn. Permissioned
 * references to Cashel Blue® / Crozier Blue® / J&L Grubb Ltd throughout.
 * Hashtag bank: 16-22 per post (Instagram engagement sweet spot, mix
 * of broad food + regional Irish + niche dairy).
 */

export const INSTAGRAM_POSTS = [
  {
    id: 'ig-floor-first',
    pillar: 'IG1',
    pillarName: 'Floor First',
    color: '#D6336C',
    format: 'Single photo · 1080×1350 portrait',
    cadence: 'Tuesday · 18:00 Dublin',
    image: '/marketing-visuals/ig1-floor-first.png',
    imageStyleTag: 'B2B SaaS · product-led · dawn creamery',
    visualBrief:
      'Asif at the Cashel Blue make-vat, 6:30am. Navy quarter-zip. Cheesecloth in soft foreground focus. Tipperary winter morning light coming through high creamery windows. Shot on iPhone, available light only.',
    title: '6:32 am. Fethard, Co. Tipperary.',
    body: `6:32 am. Fethard, Co. Tipperary.

The make-day starts in semi-darkness. By 7am the curd is forming. By 9am the moulds are filled.

I've been doing these mornings for 18 months now, notebook in hand, asking the same kinds of questions:

→ How do you decide when the curd is ready?
→ What's your moisture target?
→ Why is the temperature 31.5°C and not 32°?
→ Why a wooden ladle, not stainless?

Every answer ended up shaping a screen in Keystone.

You can't design software for a production floor from a Dublin office. You build it from inside the operation, with the people who run it, in the cold light of a make-day morning.

That's the whole methodology.

✦ Cashel Blue® and Crozier Blue® are registered trademarks of J&L Grubb Ltd. Posted with their written permission.`,
    hashtags:
      '#IrishDairy #FarmhouseCheese #Tipperary #MadeInIreland #ArtisanCheese #IrishFood #FoodManufacturing #CashelBlue #FoodTech #BehindTheScenes #BordBia #IrishBusiness #FoodTraceability #BuiltInIreland #FoundersJourney #FoodSafety #SmallBusinessIreland #Cheesemaking',
  },
  {
    id: 'ig-tools-not-theatre',
    pillar: 'IG2',
    pillarName: 'Tools, Not Theatre',
    color: '#7B5CFF',
    format: 'Single photo · 1080×1350 portrait',
    cadence: 'Thursday · 18:00 Dublin',
    image: '/marketing-visuals/ig2-tools-not-theatre.png',
    imageStyleTag: 'B2B SaaS · product-led · MacBook hero',
    visualBrief:
      'iPad locked to the silo-tracking screen, mounted on a stainless-steel control panel. A gloved hand reaches in from the right edge of the frame, wash-down hose in soft background. Slightly damp surfaces, wet area sleeve visible.',
    title: 'The actual test of food software.',
    body: `The actual test of food software:

→ Can a gloved hand operate it?
→ Does it survive the wash-down?
→ Does it work when the laptop in the office is at 3% battery and the WiFi is patchy at the dispatch dock?

Software designed in offices fails at all three.

We designed Keystone on the floor, at the dispatch table on a Friday afternoon, at the make-vat at 6:30am, at the cellar door when the inspector arrived unannounced.

What you're looking at: a real iPad, in a real wet-area sleeve, mounted on a real stainless-steel control panel, running Keystone's silo intake module at a real Irish dairy.

Not a render. Not a stock photo. Not a "vision."

✦ The dairy is Cashel Farmhouse Cheesemakers, Tipperary. Posted with their permission.`,
    hashtags:
      '#FoodSoftware #FoodTech #IrishDairy #BuiltOnTheFloor #ToolsNotTheatre #FoodManufacturing #FarmhouseCheese #ProductDesign #FoodTraceability #BRCGS #FSAI #MadeInIreland #Tipperary #SaaSIreland #SmallBusinessSoftware #PracticalSoftware #FoodIndustry #ProductionFloor',
  },
  {
    id: 'ig-cheese-is-craft',
    pillar: 'IG3',
    pillarName: 'Cheese is Craft',
    color: '#1F7A4C',
    format: 'Single photo · 1080×1080 square',
    cadence: 'Saturday · 18:00 Dublin',
    image: '/marketing-visuals/ig3-cheese-is-craft.png',
    imageStyleTag: 'McKinsey-grade editorial infographic',
    visualBrief:
      'Cashel Blue wheel cross-section on a wooden board. Slate-grey textured background. Flash of green-blue vein clearly visible. Natural side light from a window, no flash. Cheese is the hero, board, knife, single sprig of thyme as supporting cast only.',
    title: 'The blue does what it wants.',
    body: `The blue does what it wants.

It's never the same twice. Same milk, same culture, same temperature, same humidity. Different cheese.

40+ years of practice doesn't tame it. It just teaches you when to step back.

There's a quiet truth in this for software, too. The best food software doesn't try to control the operation. It records it honestly, asks fewer questions, and stays out of the way of the people who actually know the craft.

✦ @cashelblue, Cashel Blue®, Tipperary.
Photographed with permission of J&L Grubb Ltd.`,
    hashtags:
      '#CashelBlue #FarmhouseCheese #IrishCheese #ArtisanCheese #BlueCheese #Tipperary #IrishFood #BordBia #MadeInIreland #CheeseLovers #IrishDairy #FoodPhotography #SlowFood #CheesemakingTradition #IrishHeritage #CrozierBlue #SpecialityCheese #FoodCraftsmanship',
  },
  {
    id: 'ig-excel-vs-reality',
    pillar: 'IG4',
    pillarName: 'Excel vs Reality',
    color: '#C99A2E',
    format: 'Split-frame photo · 1080×1350 portrait',
    cadence: 'Tuesday · 18:00 Dublin (alternate week to Floor First)',
    image: '/marketing-visuals/ig4-excel-vs-reality.png',
    imageStyleTag: 'B · stylized editorial illustration',
    visualBrief:
      'Vertical split frame. LEFT: print-out of a 28-tab Excel workbook taped to a tiled office wall, slightly yellowed, pen marks visible. RIGHT: same operation in Keystone on a clean desktop monitor showing the traceability dashboard. Taken with available light only.',
    title: 'Two ways to do food traceability in 2026.',
    body: `Two ways to do food traceability in 2026:

← left: 28 tabs of Excel taped to a wall. Updated by hand. Searched with Ctrl+F. Backed up by emailing it to the owner on Sunday night.

→ right: one screen. Full upstream chain in under 90 seconds. Audit-ready by definition.

Both pass an audit when the auditor is kind. Only one passes when they're not.

We didn't take the photo on the left to mock anybody. That's how every Irish artisan dairy starts. It's how Cashel Blue started. It's how Marzena (their QA manager) ran the operation for 13 years before we built Keystone.

The point isn't that spreadsheets are bad. The point is that there's a moment in the life of every growing food business when the spreadsheet quietly stops being enough.

If you recognise that moment, send a DM. No slides, no pitch, 20 minutes, your numbers, my advice.`,
    hashtags:
      '#FoodTraceability #IrishDairy #FoodManufacturing #BRCGS #FSAI #AuditReady #FoodSafety #SpreadsheetProblems #SmallBusinessIreland #FoodTech #FarmhouseCheese #ArtisanFood #IrishFood #BordBia #FoodIndustry #ManufacturingSoftware #ExcelLife #DigitalTransformation',
  },
  {
    id: 'ig-the-team',
    pillar: 'IG5',
    pillarName: 'The Team',
    color: '#061A2E',
    format: 'Single photo · 1080×1080 square',
    cadence: 'Quarterly · 18:00 Dublin',
    image: '/marketing-visuals/ig5-the-team.png',
    imageStyleTag: 'Editorial · paper-meets-product flat-lay',
    visualBrief:
      "Close-up of Marzena's QA notebook. Top-down angle. Handwriting in pencil, batch numbers visible. One coffee-cup ring on the right corner. A pencil resting on the page. Soft window light. No people in frame, let the notebook tell the story.",
    title: '40 years of institutional knowledge, in one Pukka Pad.',
    body: `40 years of institutional knowledge in one Pukka Pad.

This is Marzena's QA notebook. Cashel Farmhouse Cheesemakers, Tipperary. She's been the QA manager for 13 years.

In this notebook are:

→ Every batch number for the last 18 months
→ Moisture-fat ratios for the wheels going to four different countries
→ The salt lot reference that mattered when we did the recall drill
→ Two of Sergio's phone numbers
→ One coffee ring

Most software products in our market would replace this notebook entirely. We chose not to.

Keystone is built so that Marzena keeps the notebook for the bits she likes recording by hand (the ones with personality), and the system captures what's structured, repeatable and audit-required.

The notebook still gets a coffee ring. The system still gets the audit.

That's the right division of labour.

✦ Posted with Marzena's direct permission.`,
    hashtags:
      '#QualityAssurance #IrishDairy #FarmhouseCheese #BehindTheScenes #FoodManufacturing #WomenInFood #IrishWomenInBusiness #Tipperary #ArtisanFood #FoodTraceability #InstitutionalKnowledge #CashelBlue #SmallBusinessIreland #BordBia #MadeInIreland #FoodIndustry #QAManager #FoodSafety',
  },
];
